import React, { lazy, Suspense } from "react";
import { Route, Switch } from "react-router-dom";
import { TermAppearanceType } from "@terms/models";
import { AppRoute } from "@app/models/AppRoute";
import { LocalStorageKeys } from "@app/constants/localStorage";
import TermedRoute from "@terms/containers/TermedRoute";
import AppLoadingIndicator from "@app/components/AppLoadingIndicator";
import ReportingRouter from "@reporting/containers/ReportingRouter";
import AdministrationRouter from "@administration/containers/AdministrationRouter";
import ErrorBoundary from "@app/containers/ErrorBoundary";
import ProfileRouteGuard from "@app/components/ProfileRouteGuard";
import ManageContactsRouteGuard from "@app/components/ManageContactsRouteGuard";
import DeepLinkAccessDeniedPage from "@app/components/DeepLinkAccessDeniedPage";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";
import { useGetUserPermissions } from "@user/hooks";
import { Permission } from "@user/models";
import InsightHubPage from "@insightsHub/containers/InsightHubPage";
import { isDataRoomEditable } from "@dataRoomSetup/store/selectors";
import { useAppSelector } from "@app/hooks";

const HomePage = lazy(() => import("@home/containers/HomePage"));
const DocumentsPage = lazy(() => import("@documents/containers/DocumentsPage"));
const DataRoomsPage = lazy(() => import("@dataRooms/containers/DataRoomsPage"));
const DataRoomProfilePage = lazy(() => import("@dataRooms/containers/DataRoomProfilePage"));
const DataRoomSetupPage = lazy(() => import("@dataRoomSetup/containers/DataRoomSetupPage"));
const MyProfilePage = lazy(() => import("@myProfile/containers/MyProfileFundAccountsSection"));
const ProfilePage = lazy(() => import("@profile/containers/ProfilePage"));
const FrequentlyAskedQuestionsPage = lazy(
  () => import("@frequentlyAskedQuestions/containers/FrequentlyAskedQuestionsPage")
);
const FundProfilePage = lazy(() => import("@funds/containers/FundProfilePage"));
const MyCalendarPage = lazy(() => import("@myCalendar/containers/MyCalendarPage"));
const MyCalendarEventPage = lazy(() => import("@myCalendar/containers/MyCalendarEventPage"));
const InsightsHubArticlePage = lazy(() => import("@insightsHub/containers/InsightsHubArticlePage"));
const ManageContactsPage = lazy(() => import("@manageContacts/containers/ManageContactsPage"));

const AppRouter = () => {
  const permissions = useGetUserPermissions();
  const isEdit = useAppSelector(isDataRoomEditable);

  return (
    <ErrorBoundary>
      <Suspense fallback={<AppLoadingIndicator />}>
        <Switch>
          <Route exact path={AppRoute.Home} component={HomePage} />
          <Route path={AppRoute.DocumentsDeepLink} component={DocumentsPage} />
          <Route exact path={AppRoute.Documents} component={DocumentsPage} />
          <Route path={AppRoute.InsightsHubArticle} component={InsightsHubArticlePage} />
          <Route path={AppRoute.InsightsHub} component={InsightHubPage} />

          <Route path={AppRoute.MyProfile}>
            <ProfileRouteGuard>
              <MyProfilePage />
            </ProfileRouteGuard>
          </Route>
          <Route path={AppRoute.UserProfile} component={ProfilePage} />
          {permissions[Permission.ViewDataRooms] && [
            <Route
              key={AppRoute.DataRoomVideoDeepLink}
              path={AppRoute.DataRoomVideoDeepLink}
              component={isEdit ? DataRoomSetupPage : DataRoomProfilePage}
            />,
            <Route
              key={AppRoute.DataRoomDocumentsDeepLink}
              path={AppRoute.DataRoomDocumentsDeepLink}
              component={isEdit ? DataRoomSetupPage : DataRoomProfilePage}
            />,
            <Route
              key={AppRoute.DataRoom}
              path={AppRoute.DataRoom}
              component={isEdit ? DataRoomSetupPage : DataRoomProfilePage}
            />,
            <Route key={AppRoute.DataRooms} path={AppRoute.DataRooms} component={DataRoomsPage} />,
          ]}
          {!permissions[Permission.ViewDataRooms] && [
            <Route
              key={AppRoute.DataRoomVideoDeepLink}
              path={AppRoute.DataRoomVideoDeepLink}
              render={() => <DeepLinkAccessDeniedPage resourceLabel="video" />}
            />,
            <Route
              key={AppRoute.DataRoomDocumentsDeepLink}
              path={AppRoute.DataRoomDocumentsDeepLink}
              render={() => <DeepLinkAccessDeniedPage resourceLabel="document" />}
            />,
          ]}
          <TermedRoute
            type={TermAppearanceType.OpenReportingPage}
            path={AppRoute.ReportingBase}
            component={ReportingRouter}
          />
          {permissions[Permission.ViewFunds] && [
            <TermedRoute
              key={AppRoute.FundProfile}
              exact
              type={TermAppearanceType.OpenFundProfilePage}
              path={AppRoute.FundProfile}
              component={FundProfilePage}
            />,
            <TermedRoute
              key={AppRoute.FundDocumentsDeepLink}
              type={TermAppearanceType.OpenFundProfilePage}
              path={AppRoute.FundDocumentsDeepLink}
              component={FundProfilePage}
            />,
          ]}
          {!permissions[Permission.ViewFunds] && (
            <Route
              key={AppRoute.FundDocumentsDeepLink}
              path={AppRoute.FundDocumentsDeepLink}
              render={() => <DeepLinkAccessDeniedPage resourceLabel="document" />}
            />
          )}
          {FeatureToggleService.isFeatureEnabled(ToggledFeature.MyCalendar) && [
            <Route
              key={AppRoute.MyCalendarEventVideoDeepLink}
              path={AppRoute.MyCalendarEventVideoDeepLink}
              component={MyCalendarEventPage}
            />,
            <Route
              key={AppRoute.MyCalendarEventDocumentDeepLink}
              path={AppRoute.MyCalendarEventDocumentDeepLink}
              component={MyCalendarEventPage}
            />,
            <Route key={AppRoute.MyCalendarEvent} path={AppRoute.MyCalendarEvent} component={MyCalendarEventPage} />,
            <Route key={AppRoute.MyCalendar} path={AppRoute.MyCalendar} component={MyCalendarPage} />,
          ]}
          {!FeatureToggleService.isFeatureEnabled(ToggledFeature.MyCalendar) && [
            <Route
              key={AppRoute.MyCalendarEventVideoDeepLink}
              path={AppRoute.MyCalendarEventVideoDeepLink}
              render={() => <DeepLinkAccessDeniedPage resourceLabel="video" />}
            />,
            <Route
              key={AppRoute.MyCalendarEventDocumentDeepLink}
              path={AppRoute.MyCalendarEventDocumentDeepLink}
              render={() => <DeepLinkAccessDeniedPage resourceLabel="document" />}
            />,
          ]}

          <Route path={AppRoute.AdministrationBase} component={AdministrationRouter} />
          <Route path={AppRoute.ManageContacts}>
            <ManageContactsRouteGuard>
              <ManageContactsPage />
            </ManageContactsRouteGuard>
          </Route>
          <Route path={AppRoute.Help} component={FrequentlyAskedQuestionsPage} />

          <Route
            path={AppRoute.Login}
            component={() => {
              window.location.assign(process.env.REACT_APP_AUTH_LOGIN_URI!);

              return <AppLoadingIndicator />;
            }}
          />
          <Route
            path={AppRoute.SignOut}
            component={() => {
              localStorage.removeItem(LocalStorageKeys.LpcActiveSession);
              window.location.assign(process.env.REACT_APP_AUTH_SIGNOUT_URI!);

              return <AppLoadingIndicator />;
            }}
          />
        </Switch>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRouter;
